import { Column } from "../../components/DataTable";
import { Button } from "../../components/ui/button";
import { renderApplicationsCell, renderTypeBadge } from "../AssetCoverageOnboarding/columns";
import { getApplicationMappings } from "../AssetCoverageOnboarding/services";
import type { AgentHealthAssetRow, AgentHealthAssetsRequest, AgentHealthAssetSortKey } from "./services";

export type AgentStatus = "Active" | "Inactive" | "Unreachable" | "Never Reported";

export interface ToolDetailRow {
  tool: string;
  version: string;
  status: string;
  daysSinceReporting: string;
  lastHeartbeat: string;
}

export function getToolStatusStyle(status: string | null | undefined): {
  pill: string;
  dot: string;
} {
  const normalized = (status || "").trim().toLowerCase().replace(/_/g, " ");

  if (normalized === "active") {
    return {
      pill: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      dot: "bg-emerald-500",
    };
  }
  if (normalized === "inactive") {
    return {
      pill: "border-orange-500/30 bg-orange-500/10 text-orange-600 dark:text-orange-400",
      dot: "bg-orange-500",
    };
  }
  if (normalized === "unreachable") {
    return {
      pill: "border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400",
      dot: "bg-red-500",
    };
  }
  if (normalized === "never reported" || normalized === "never") {
    return {
      pill: "border-slate-500/30 bg-slate-500/10 text-slate-600 dark:text-slate-400",
      dot: "bg-slate-400 dark:bg-slate-500",
    };
  }
  return {
    pill: "border-border bg-muted/60 text-muted-foreground",
    dot: "bg-muted-foreground/60",
  };
}

// Same badge shape as the Type column, colored by tool status.
const TOOL_BADGE_COLORS: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  inactive: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  unreachable: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  "never reported": "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400",
};

const toolBadgeClass = (status: string) => {
  const key = status.trim().toLowerCase().replace(/_/g, " ");
  const color =
    TOOL_BADGE_COLORS[key === "never" ? "never reported" : key] ?? "bg-muted text-muted-foreground";
  return `inline-flex w-[130px] items-center justify-center whitespace-nowrap px-2 py-0.5 rounded-md text-xs font-medium ${color}`;
};

export const TOOL_DETAIL_COLUMNS: Column<ToolDetailRow>[] = [
  {
    key: "tool",
    header: "Tool",
    render: (row) => <span className="text-foreground">{row.tool || "-"}</span>,
  },
  {
    key: "version",
    header: "Version",
    render: (row) => (
      <span className="tabular-nums text-muted-foreground">{row.version || "-"}</span>
    ),
  },
  {
    key: "status",
    header: "Status",
    align: "center",
    headerAlign: "center",
    render: (row) => {
      if (!row.status || row.status === "-") {
        return <span className="text-muted-foreground">-</span>;
      }
      const { pill, dot } = getToolStatusStyle(row.status);
      return (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${pill}`}
        >
          <span aria-hidden="true" className={`size-1.5 rounded-full ${dot}`} />
          {row.status}
        </span>
      );
    },
  },
  {
    key: "daysSinceReporting",
    header: "Days Since Reporting",
    align: "center",
    headerAlign: "center",
    render: (row) => (
      <span className="tabular-nums text-muted-foreground">{row.daysSinceReporting ?? "-"}</span>
    ),
  },
  {
    key: "lastHeartbeat",
    header: "Last Heartbeat",
    render: (row) => (
      <span className="tabular-nums text-muted-foreground">{row.lastHeartbeat || "-"}</span>
    ),
  },
];

/** Dynamic tool columns use `tool_<toolName>` keys; the suffix is sent as `sortTool`. */
const AGENT_TOOL_SORT_PREFIX = "tool_";

/** Sort fields for the assets request: `sortTool` for a tool column, `sortKey` otherwise. */
export function getAgentHealthSortPayload(
  sortKey: string | null,
  sortDir: "asc" | "desc",
): Pick<AgentHealthAssetsRequest, "sortKey" | "sortTool" | "sortDir"> {
  if (!sortKey) return {};
  if (sortKey.startsWith(AGENT_TOOL_SORT_PREFIX)) {
    return { sortTool: sortKey.slice(AGENT_TOOL_SORT_PREFIX.length), sortDir };
  }
  return { sortKey: sortKey as AgentHealthAssetSortKey, sortDir };
}

/** Columns for the live POST /agent-health/assets roster, shared by the main table and every drilldown sheet. Sorting is server-side.
 * Tool columns are built from the `tools` object on the response rows. */
export function getAgentHealthAssetColumns(
  rows: AgentHealthAssetRow[],
  onOpenTools: (asset: AgentHealthAssetRow, tool: string) => void,
): Column<AgentHealthAssetRow>[] {
  const toolKeys: string[] = [];
  rows.forEach((r) =>
    Object.keys(r.tools ?? {}).forEach((k) => {
      if (!toolKeys.includes(k)) toolKeys.push(k);
    }),
  );

  const toolColumns: Column<AgentHealthAssetRow>[] = toolKeys.map((toolKey) => ({
    key: `${AGENT_TOOL_SORT_PREFIX}${toolKey}`,
    header: toolKey,
    sortable: true,
    align: "center",
    headerAlign: "center",
    width: "200px",
    render: (row) => {
      const value = row.tools?.[toolKey];
      if (!value) return <span className="text-xs text-muted-foreground">-</span>;
      return (
        <Button
          type="button"
          variant="none"
          size="none"
          onClick={() => onOpenTools(row, toolKey)}
          title="View tool details"
          className={`cursor-pointer hover:opacity-80 ${toolBadgeClass(value)}`}
        >
          {value}
        </Button>
      );
    },
  }));

  return [
    {
      key: "assetName",
      header: "Asset Name",
      align: "left",
      headerAlign: "center",
      sortable: true,
      width: "240px",
      minWidth: 220,
      render: (row) => <span className="text-xs font-normal text-foreground">{row.assetName || "-"}</span>,
    },
    { key: "type", header: "Type", align: "center", headerAlign: "center", sortable: true, width: "160px", minWidth: 150, render: renderTypeBadge },
    {
      key: "ip",
      header: "IP Address",
      align: "center",
      headerAlign: "center",
      sortable: true,
      width: "240px",
      minWidth: 220,
      render: (row) => (
        <span className="text-xs font-normal text-foreground tabular-nums">{row.ip || "-"}</span>
      ),
    },
    {
      key: "environment",
      header: "Environment",
      align: "center",
      headerAlign: "center",
      sortable: true,
      width: "130px",
      minWidth: 120,
    },
    {
      key: "application",
      header: "Applications",
      sortable: true,
      align: "left",
      headerAlign: "center",
      width: "240px",
      minWidth: 230,
      render: (row) =>
        renderApplicationsCell(getApplicationMappings({ applications: row.applications, application: row.application })),
    },
    ...toolColumns,
  ];
}
